import { useState, type ReactNode } from 'react';
import type { ContractEffort, EffortPerson } from '@forecastify/shared';
import {
  DataList,
  DropdownMenu,
  Icon,
  type Cell,
  type DataListColumn,
  type DataListRow,
  type FilterOption,
  type SortState,
} from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../../i18n';
import { identityColor, initialsOf } from '../../identity';
import { listLabels } from '../../delivery/clients-screen';
import { useCalendarLabels } from '../../date-field';
import { sortInMemory } from '../../sort-in-memory';
import { useRowPages } from '../../use-row-pages';
import {
  REGISTER_PAGE_SIZE,
  dateRangeFilter,
  lineItemTypeLabel,
  lineItemTypeOptions,
  matches,
  withinRange,
} from '../register-tools';
import { financeTipContent } from '../finance/finance-tip';
import { hours, index, percent, signedCount, signedHours } from './effort-format';
import type { EffortModel } from './effort-model';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C65 · the registers under the chart: periods, deliverables, tasks, people.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **PERIODS FOR A RETAINER LOOKED AT WHOLE, DELIVERABLES FOR EVERYTHING ELSE — AND THE
 * COLUMNS ARE NOT THE SAME.** A period has a state and a frame it either held or did not; a
 * deliverable has a type, a scope and an earned value. One table serving both would be half blank
 * on every row, which is how a table stops being read. `finance-tables.tsx` made the same split
 * for the same reason.
 *
 * ⚠️ **The tasks table is always there, beside the other one.** The mockup reaches it by drilling
 * into a deliverable; ours renders it directly, because the read already carries every column it
 * needs and a drill-down would mean a third scope on the wire for no new information. What that
 * costs is the filter — the table lists the scope's tasks rather than one deliverable's.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ `DataList` NOW — ux-pass-2, 14 Oct 2026, overruling the note that stood here. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * It said *"Not `DataList`. These are ledgers … where sorting the periods by usage would break the
 * chronology."* The product owner's list asks for the unified list on all four by name — the
 * multi-column header sort, the toolbar's search and filters, the settled pager. The chronology
 * survives: **an unsorted register is in the order the wire sent it**, and `sortInMemory` returns
 * exactly that for an empty sort, so nobody loses it until they click a header.
 *
 * ⚠️ **The column tooltips came along** — every head keeps its `?`, through `DataListColumn.tip`
 * and in `FinanceTip`'s own layout (`useHeadTip`).
 *
 * ⚠️ **What the move cost is the row tints**: the running period was tinted and future ones were greyed at the `<tr>`.
 * `DataList` styles rows itself, so the state cell carries it now — a filled badge on the running
 * period, muted figures on future ones.
 */

const NONE = ':none';

function bad(condition: boolean): string {
  return condition ? 'text-destructive' : '';
}

/**
 * A column head's explanation — the same name-and-meaning pair the hand-built heads carried, in
 * `FinanceTip`'s own layout, so a register's `?` reads exactly like a tile's.
 */
function useHeadTip(): (name: MessageKey, what: MessageKey) => ReactNode {
  const t = useT();
  const calc = t('finance.calc');
  return (name, what) => financeTipContent({ name: t(name), what: t(what) }, calc);
}

/** A figure in a column — tabular, and muted where the row is not live yet. */
function figure(text: string, extra: Partial<Cell> = {}): Cell {
  return { text, mono: true, ...extra };
}

/** The title strip every register keeps above its list, with an optional right-hand slot. */
function Register({
  title,
  aside,
  children,
}: {
  readonly title: string;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="bg-card overflow-hidden rounded-xl border shadow-sm">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {title}
        </div>
        <div className="flex-1" />
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Distinct values as filter options, sorted the way the lists sort text. */
function distinct(values: readonly (string | null)[], noneLabel: string): FilterOption[] {
  const named = [...new Set(values.filter((one): one is string => one !== null && one !== ''))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map((one) => ({ value: one, label: one }));
  /* ⚠️ *Nincs megadva* only when some row has nothing — an option that matches no row is a dead
     end the reader has to try to discover. */
  return values.some((one) => one === null || one === '')
    ? [...named, { value: NONE, label: noneLabel }]
    : named;
}

function picks(value: string | null, filter: string): boolean {
  if (filter === '') return true;
  if (filter === NONE) return value === null || value === '';
  return value === filter;
}

/* ── the periods of a retainer ─────────────────────────────────────────────── */

/**
 * ⚠️⚠️ **THIS TABLE AND THE DELIVERABLES TABLE ARE BOTH DRAWN, NOT ONE OR THE OTHER.** They used to
 * be one component that picked — and on a retainer seen whole that meant the deliverables table was
 * absent entirely, which is where the frame comes from. The mockup renders both, in this order:
 * what the contract promised, then how each period of it went.
 */
export function EffortPeriodsTable({
  view,
  model,
  onOpenPeriod,
}: {
  readonly view: ContractEffort;
  readonly model: EffortModel;
  readonly onOpenPeriod: (startsOn: string) => void;
}) {
  const t = useT();
  const tip = useHeadTip();
  const [query, setQuery] = useState('');
  const [state, setState] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);

  const shown = view.periods.filter(
    (row) =>
      (state === '' || row.state === state) &&
      matches([`#${String(row.index)}`, row.startsOn, row.endsOn], query),
  );

  const rows: DataListRow[] = sortInMemory(shown, sort, {
    index: (row) => row.index,
    period: (row) => row.startsOn,
    state: (row) => (row.state === 'past' ? 0 : row.state === 'current' ? 1 : 2),
    frame: (row) => Number(row.budget),
    plan: (row) => Number(row.planned),
    actual: (row) => (row.state === 'future' ? null : Number(row.logged)),
    variance: (row) => (row.state === 'future' ? null : Number(row.logged) - Number(row.budget)),
    share: (row) =>
      row.state === 'future' || Number(row.budget) <= 0.004
        ? null
        : Number(row.logged) / Number(row.budget),
    overtime: (row) => Number(row.overtime),
    billable: (row) => Number(row.billable),
  }).map((row) => {
    const frame = Number(row.budget);
    const used = Number(row.logged);
    const gap = used - frame;
    const future = row.state === 'future';
    return {
      id: row.startsOn,
      cells: {
        index: figure(`#${String(row.index)}`, { muted: future }),
        period: figure(`${row.startsOn} – ${row.endsOn}`, { muted: future }),
        /* ⚠️ The RUNNING period is the filled badge — a reader looking for "where are we now"
           should not have to compare dates. It used to be a tinted row. */
        state: {
          badge:
            row.state === 'past'
              ? { label: t('effort.state.past'), variant: 'secondary' as const }
              : row.state === 'current'
                ? { label: t('effort.state.current'), variant: 'default' as const }
                : { label: t('effort.state.future'), variant: 'outline' as const },
        },
        frame: figure(hours(frame), { muted: true }),
        plan:
          Number(row.planned) > 0.004
            ? figure(hours(Number(row.planned)), { muted: future })
            : null,
        actual: future ? null : figure(hours(used), { bold: true }),
        variance: future ? null : figure(signedHours(gap), { className: bad(gap > 0.004) }),
        share:
          future || frame <= 0.004
            ? null
            : figure(percent(used / frame), { className: bad(used > frame + 0.004) }),
        overtime: Number(row.overtime) > 0.004 ? figure(hours(Number(row.overtime))) : null,
        billable: Number(row.billable) > 0.004 ? figure(hours(Number(row.billable))) : null,
      },
    };
  });

  const paged = useRowPages(rows, JSON.stringify([query, state, sort]), REGISTER_PAGE_SIZE);

  /* ⚠️ The Σ counts what the filters LEFT, not the page — a total that changed when somebody
     pressed Next would be a different number. Future periods add no actual, as on the row. */
  const sum = (pick: (row: ContractEffort['periods'][number]) => string): number =>
    shown.reduce((total, row) => total + Number(pick(row)), 0);
  const spent = shown.reduce(
    (total, row) => total + (row.state === 'future' ? 0 : Number(row.logged)),
    0,
  );
  const totals = {
    label:
      state === '' && query.trim() === ''
        ? t('effort.table.total')
        : t('effort.table.totalFiltered'),
    frame: hours(sum((row) => row.budget)),
    plan: hours(sum((row) => row.planned)),
    actual: hours(spent),
    overtime: hours(sum((row) => row.overtime)),
  };

  const columns: DataListColumn[] = [
    {
      key: 'index',
      tip: tip('effort.tip.periodIndex.name', 'effort.tip.periodIndex.what'),
      label: '#',
      width: 56,
      sortKey: 'index',
    },
    {
      key: 'period',
      tip: tip('effort.tip.periodSpan.name', 'effort.tip.periodSpan.what'),
      label: t('effort.col.period'),
      sortKey: 'period',
    },
    {
      key: 'state',
      tip: tip('effort.tip.periodState.name', 'effort.tip.periodState.what'),
      label: t('effort.col.state'),
      width: 104,
      sortKey: 'state',
    },
    {
      key: 'frame',
      tip: tip('effort.tip.periodFrame.name', 'effort.tip.periodFrame.what'),
      label: t('effort.col.frame'),
      width: 88,
      align: 'right',
      sortKey: 'frame',
    },
    {
      key: 'plan',
      tip: tip('effort.tip.plan.name', 'effort.tip.plan.what'),
      label: t('effort.col.plan'),
      width: 88,
      align: 'right',
      sortKey: 'plan',
    },
    {
      key: 'actual',
      tip: tip('effort.tip.actual.name', 'effort.tip.actual.what'),
      label: t('effort.col.actual'),
      width: 88,
      align: 'right',
      sortKey: 'actual',
    },
    {
      key: 'variance',
      tip: tip('effort.tip.periodVariance.name', 'effort.tip.periodVariance.what'),
      label: t('effort.col.variance'),
      width: 96,
      align: 'right',
      sortKey: 'variance',
    },
    {
      key: 'share',
      tip: tip('effort.tip.periodShare.name', 'effort.tip.periodShare.what'),
      label: t('effort.col.share'),
      width: 104,
      align: 'right',
      sortKey: 'share',
    },
    {
      key: 'overtime',
      tip: tip('effort.tip.overtime.name', 'effort.tip.overtime.what'),
      label: t('effort.col.overtime'),
      width: 88,
      align: 'right',
      sortKey: 'overtime',
    },
    ...(view.hasBillable
      ? [
          {
            key: 'billable',
            tip: tip('effort.tip.billable.name', 'effort.tip.billable.what'),
            label: t('effort.col.billable'),
            width: 96,
            align: 'right' as const,
            sortKey: 'billable',
          },
        ]
      : []),
  ];

  return (
    <Register
      title={t('effort.table.periods')}
      aside={
        <span className="text-muted-foreground text-[11px] tabular-nums">
          {t('effort.table.periodsNote', {
            count: String(view.periods.length),
            over: String(model.overspentPeriods),
          })}
        </span>
      }
    >
      <DataList
        /* 30 Oct 2026 · sized to its rows: `h-full` took the card's whole height BELOW its header,
           so the pager overflowed the card and was clipped — only ten rows could ever be seen. */
        className="h-auto"
        columnsKey="effort.periods"
        selectable={false}
        columns={columns}
        rows={paged.rows}
        page={paged.page}
        sort={sort}
        onSortChange={setSort}
        search={query}
        onSearchChange={setQuery}
        filters={[
          {
            key: 'state',
            label: t('effort.filter.state'),
            value: state,
            onChange: setState,
            options: [
              { value: 'past', label: t('effort.state.past') },
              { value: 'current', label: t('effort.state.current') },
              { value: 'future', label: t('effort.state.future') },
            ],
          },
        ]}
        onRowClick={onOpenPeriod}
        empty={
          view.periods.length === 0
            ? { icon: 'calendar', title: t('effort.tools.noPeriods'), body: '' }
            : {
                icon: 'search',
                title: t('effort.tools.noMatch'),
                body: t('effort.tools.noMatchBody'),
              }
        }
        footerNote={
          view.hasBillable
            ? t('effort.table.periodTotalsBillable', {
                ...totals,
                billable: hours(sum((row) => row.billable)),
              })
            : t('effort.table.periodTotals', totals)
        }
        labels={{ ...listLabels(t), table: t('effort.table.periods') }}
      />
    </Register>
  );
}

/* ── the deliverables ──────────────────────────────────────────────────────── */

export function EffortLineItemsTable({
  view,
  model,
  onOpenLineItem,
}: {
  readonly view: ContractEffort;
  readonly model: EffortModel;
  readonly onOpenLineItem: (id: string) => void;
}) {
  const t = useT();
  const tip = useHeadTip();
  const calendar = useCalendarLabels();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [type, setType] = useState('');
  /* ⚠️ Round 5 item 8. `retainer` / `billable` is the one narrowing a deliverable HAS — it is
     the column that decides whether the row counts against the frame at all. */
  const [scope, setScope] = useState('');
  /* ⚠️ ux-pass-2 — start and end as ranges, the contracts list's own two. */
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');

  /**
   * ⚠️⚠️ **THE SCOPE FILTER EXISTS ONLY WHERE THE SCOPE COLUMN DOES.** It used to show on every
   * contract while the column rendered on recurring ones only — so a fixed-price contract offered
   * a filter over a value no row displayed, and every deliverable on it is `retainer` by default.
   * The predicate ignores a stale value too, should the contract change under a set filter.
   *
   * ⚠️ U-4 · on the whole project the view is not recurring, yet a retainer's billable deliverable
   * can be among the rows — so there the column is drawn wherever some row is billable.
   */
  const scoped = view.wholeProject
    ? view.lineItems.some((one) => one.scope === 'billable')
    : view.recurring;

  const shown = view.lineItems.filter(
    (one) =>
      matches([one.name, one.identifier, one.contractIdentifier, one.contractName], query) &&
      (type === '' || one.type === type) &&
      (!scoped || scope === '' || one.scope === scope) &&
      /* ⚠️ A deliverable with no start of its own starts with the contract — the row prints that
         date, so the filter reads the same one. */
      withinRange(one.startsOn ?? view.from, startRange) &&
      /* ⚠️ `null` is *nyitott*: later than any bound, `withinRange` argues it. */
      withinRange(one.endsOn, endRange),
  );
  const filtered =
    query.trim() !== '' ||
    type !== '' ||
    (scoped && scope !== '') ||
    startRange !== '' ||
    endRange !== '';

  const rows: DataListRow[] = sortInMemory(shown, sort, {
    index: (row) => row.number,
    name: (row) => row.name,
    contract: (row) => row.contractIdentifier,
    billing: (row) => lineItemTypeLabel(t, row.type),
    scope: (row) => row.scope,
    startsOn: (row) => row.startsOn ?? view.from,
    /* ⚠️ Open-ended sorts as the latest end, in both directions — not as a blank. */
    endsOn: (row) => row.endsOn ?? '9999-12-31',
    frame: (row) => (row.budget === null ? null : Number(row.budget)),
    plan: (row) => Number(row.planned),
    actual: (row) => Number(row.logged),
    earned: (row) => (row.taskCount > 0 ? Number(row.earned) : null),
    cpi: (row) =>
      Number(row.logged) > 0.004 && row.taskCount > 0
        ? Number(row.earned) / Number(row.logged)
        : null,
    done: (row) => (row.taskCount === 0 ? null : row.doneTasks / row.taskCount),
    slip: (row) => row.slipWorkdays,
  }).map((row) => {
    const position = view.lineItems.indexOf(row);
    const frame = row.budget === null ? null : Number(row.budget);
    const used = Number(row.logged);
    const earned = Number(row.earned);
    const cpi = used > 0.004 && row.taskCount > 0 ? earned / used : null;
    return {
      id: row.id,
      cells: {
        index: figure(`${String(position + 1)}.`, { muted: true }),
        name: { text: row.name, bold: true },
        /* ⚠️ R-57 · the contract's public identifier, its name beneath. */
        contract: figure(row.contractIdentifier, { muted: true, sub: row.contractName }),
        billing: { text: lineItemTypeLabel(t, row.type), muted: true },
        scope:
          row.scope === 'billable'
            ? { text: t('effort.scope.billable'), className: 'text-primary font-medium' }
            : { text: t('effort.scope.retainer'), muted: true },
        startsOn: figure(row.startsOn ?? view.from, { muted: true }),
        /* ⚠️ «nyitott» rather than the contract's end date: an open-ended deliverable has no end
           of its own, and printing one states a term nobody agreed. */
        endsOn:
          row.endsOn === null
            ? { text: t('effort.open'), muted: true }
            : figure(row.endsOn, { muted: true }),
        frame: frame === null ? null : figure(hours(frame), { muted: true }),
        plan: Number(row.planned) > 0.004 ? figure(hours(Number(row.planned))) : null,
        actual: used > 0.004 ? figure(hours(used), { bold: true }) : null,
        earned: row.taskCount > 0 ? figure(hours(earned), { muted: true }) : null,
        cpi: cpi === null ? null : figure(index(cpi), { className: bad(cpi < 0.92) }),
        done:
          row.taskCount === 0 ? null : figure(`${String(row.doneTasks)}/${String(row.taskCount)}`),
        slip:
          row.slipWorkdays === null
            ? null
            : figure(signedCount(row.slipWorkdays, '').trim(), { className: 'text-destructive' }),
      },
    };
  });

  const paged = useRowPages(
    rows,
    JSON.stringify([query, sort, type, scope, startRange, endRange]),
    REGISTER_PAGE_SIZE,
  );

  /**
   * ⚠️⚠️ **TWO TOTALS, AND THE LABEL SAYS WHICH.** Unfiltered, the Σ is the SCOPE's — the model's
   * frame, plan, actual and earned value, which leave billable deliverables out of the frame and
   * so are not the sum of the column above. That is the figure the tiles print and it stays.
   * Filtered, it is the sum of the rows the filters left, because a total that ignored the filter
   * would be describing rows the reader just hid.
   */
  const footerNote = ((): string => {
    if (!filtered) {
      return t('effort.table.lineItemTotals', {
        label: t('effort.table.total'),
        frame: hours(model.budget),
        plan: hours(model.planned),
        actual: hours(model.logged),
        earned: hours(model.earned),
        cpi: index(model.cpi),
      });
    }
    const sum = (pick: (row: (typeof shown)[number]) => number): number =>
      shown.reduce((total, row) => total + pick(row), 0);
    const logged = sum((row) => Number(row.logged));
    const earned = sum((row) => (row.taskCount > 0 ? Number(row.earned) : 0));
    return t('effort.table.lineItemTotals', {
      label: t('effort.table.totalFiltered'),
      frame: hours(sum((row) => (row.budget === null ? 0 : Number(row.budget)))),
      plan: hours(sum((row) => Number(row.planned))),
      actual: hours(logged),
      earned: hours(earned),
      cpi: index(logged > 0.004 ? earned / logged : null),
    });
  })();

  const columns: DataListColumn[] = [
    {
      key: 'index',
      tip: tip('effort.tip.lineItemIndex.name', 'effort.tip.lineItemIndex.what'),
      label: '#',
      width: 52,
      sortKey: 'index',
    },
    {
      key: 'name',
      tip: tip('effort.tip.lineItem.name', 'effort.tip.lineItem.what'),
      label: t('effort.col.lineItem'),
      sortKey: 'name',
    },
    /* ⚠️ U-4 · only on the whole project: on one contract every row would print the same one. */
    ...(view.wholeProject
      ? [
          {
            key: 'contract',
            tip: tip('effort.tip.contract.name', 'effort.tip.contract.what'),
            label: t('effort.col.contract'),
            width: 168,
            sortKey: 'contract',
          },
        ]
      : []),
    {
      key: 'billing',
      tip: tip('effort.tip.billing.name', 'effort.tip.billing.what'),
      label: t('effort.col.billing'),
      width: 136,
      sortKey: 'billing',
    },
    ...(scoped
      ? [
          {
            key: 'scope',
            tip: tip('effort.tip.scope.name', 'effort.tip.scope.what'),
            label: t('effort.col.scope'),
            width: 112,
            sortKey: 'scope',
          },
        ]
      : []),
    {
      key: 'startsOn',
      tip: tip('effort.tip.startsOn.name', 'effort.tip.startsOn.what'),
      label: t('effort.col.startsOn'),
      width: 104,
      sortKey: 'startsOn',
    },
    {
      key: 'endsOn',
      tip: tip('effort.tip.endsOn.name', 'effort.tip.endsOn.what'),
      label: t('effort.col.endsOn'),
      width: 104,
      sortKey: 'endsOn',
    },
    {
      key: 'frame',
      tip: tip('effort.tip.itemFrame.name', 'effort.tip.itemFrame.what'),
      label: t('effort.col.frame'),
      width: 88,
      align: 'right',
      sortKey: 'frame',
    },
    {
      key: 'plan',
      tip: tip('effort.tip.plan.name', 'effort.tip.plan.what'),
      label: t('effort.col.plan'),
      width: 88,
      align: 'right',
      sortKey: 'plan',
    },
    {
      key: 'actual',
      tip: tip('effort.tip.actual.name', 'effort.tip.actual.what'),
      label: t('effort.col.actual'),
      width: 88,
      align: 'right',
      sortKey: 'actual',
    },
    {
      key: 'earned',
      tip: tip('effort.tip.ev.name', 'effort.tip.ev.what'),
      label: t('effort.col.earned'),
      width: 112,
      align: 'right',
      sortKey: 'earned',
    },
    {
      key: 'cpi',
      tip: tip('effort.tip.cpi.name', 'effort.tip.cpi.what'),
      label: 'CPI',
      width: 68,
      align: 'right',
      sortKey: 'cpi',
    },
    {
      key: 'done',
      tip: tip('effort.tip.doneTasks.name', 'effort.tip.doneTasks.what'),
      label: t('effort.col.done'),
      width: 76,
      align: 'right',
      sortKey: 'done',
    },
    {
      key: 'slip',
      tip: tip('effort.tip.itemSlip.name', 'effort.tip.itemSlip.what'),
      label: t('effort.col.slip'),
      width: 92,
      align: 'right',
      sortKey: 'slip',
    },
  ];

  return (
    <Register
      title={
        view.selectedPeriod === null
          ? t('effort.table.lineItems')
          : t('effort.table.lineItemsInPeriod')
      }
    >
      {/* ⚠️⚠️ **THE FOUR FIGURES ARE GONE — round 5 item 9.** *"4 sortétel · keret 620:00 ·
          terv 200:00 · tény 77:45"* restated, in the corner above a table, four numbers the table
          itself prints in full — one of them a row count somebody can see by looking. */}
      <DataList
        /* 30 Oct 2026 · sized to its rows: `h-full` took the card's whole height BELOW its header,
           so the pager overflowed the card and was clipped — only ten rows could ever be seen. */
        className="h-auto"
        columnsKey="effort.lineItems"
        selectable={false}
        columns={columns}
        rows={paged.rows}
        page={paged.page}
        sort={sort}
        onSortChange={setSort}
        search={query}
        onSearchChange={setQuery}
        filters={[
          {
            key: 'type',
            label: t('effort.filter.billing'),
            value: type,
            onChange: setType,
            options: lineItemTypeOptions(t),
          },
          ...(scoped
            ? [
                {
                  key: 'scope',
                  label: t('effort.filter.scope'),
                  value: scope,
                  onChange: setScope,
                  options: [
                    { value: 'retainer', label: t('effort.scope.retainer') },
                    { value: 'billable', label: t('effort.scope.billable') },
                  ],
                },
              ]
            : []),
          dateRangeFilter(t, calendar, {
            key: 'startsOn',
            label: t('effort.filter.startsOn'),
            value: startRange,
            onChange: setStartRange,
          }),
          dateRangeFilter(t, calendar, {
            key: 'endsOn',
            label: t('effort.filter.endsOn'),
            value: endRange,
            onChange: setEndRange,
          }),
        ]}
        onRowClick={onOpenLineItem}
        empty={
          filtered
            ? {
                icon: 'search',
                title: t('effort.tools.noMatch'),
                body: t('effort.tools.noMatchBody'),
              }
            : {
                icon: 'file-text',
                title: t('effort.empty.noLineItem'),
                body: t('effort.empty.noLineItemBody'),
              }
        }
        footerNote={footerNote}
        labels={{ ...listLabels(t), table: t('effort.table.lineItems') }}
      />
    </Register>
  );
}

/* ── the tasks, with their own EVM columns ─────────────────────────────────── */

/**
 * ⚠️⚠️ **LARGEST GAP FIRST BY DEFAULT — ux-pass-2.** *Eltérés* is logged minus earned, so a positive
 * one — the red ones — is time spent that bought no progress. Those are the rows somebody opens
 * this table to find, and putting them on page one is the whole of the request. A module constant
 * so the initial state is one reference, as `DataList`'s own `EMPTY_SORT` argues.
 */
const TASKS_DEFAULT_SORT: readonly SortState[] = [{ key: 'variance', direction: 'desc' }];

export function EffortTasksTable({
  view,
  onOpenTask,
}: {
  readonly view: ContractEffort;
  readonly model: EffortModel;
  readonly onOpenTask: (id: string) => void;
}) {
  const t = useT();
  const tip = useHeadTip();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>(TASKS_DEFAULT_SORT);
  /* ⚠️ Round 5 item 8. The STATUS CATEGORY — kept beside the status itself, because a
     workspace's own statuses are its vocabulary and the category is the one question that reads
     the same on every project: *what is still open*. */
  const [category, setCategory] = useState('');
  /* ⚠️ ux-pass-2 — the status by NAME too, and the assignee. The wire carries names and no ids
     for either, so both filter by the name the row prints; the options are the names present. */
  const [status, setStatus] = useState('');
  const [assignee, setAssignee] = useState('');

  const shown = view.tasks.filter(
    (one) =>
      (category === '' || one.statusCategory === category) &&
      picks(one.statusName, status) &&
      picks(one.assigneeName, assignee) &&
      matches(
        [one.key, one.title, one.assigneeName, one.lineItemName, one.contractIdentifier],
        query,
      ),
  );
  const late = (row: (typeof view.tasks)[number]): boolean =>
    row.dueOn !== null && row.dueOn < view.today && row.statusCategory !== 'closed';

  const rows: DataListRow[] = sortInMemory(shown, sort, {
    key: (row) => row.key,
    title: (row) => row.title,
    lineItem: (row) => row.lineItemName,
    assignee: (row) => row.assigneeName,
    status: (row) => row.statusName,
    estimate: (row) => Number(row.estimate),
    logged: (row) => Number(row.logged),
    weight: (row) => Number(row.weight),
    earned: (row) => Number(row.earned),
    variance: (row) => Number(row.logged) - Number(row.earned),
    due: (row) => row.dueOn,
  }).map((row) => {
    const logged = Number(row.logged);
    const earned = Number(row.earned);
    const gap = logged - earned;
    return {
      id: row.id,
      cells: {
        /* ⚠️ A key never wraps: `ABCDEFGH-123` on two lines split the row height. `DataList`
           cells are `whitespace-nowrap` and the column is 120px wide for the same reason. */
        key: figure(row.key, { muted: true, className: 'whitespace-nowrap' }),
        title: { text: row.title, bold: true },
        /* ⚠️ U-4 · a task with no line item says so — its hours still count. */
        lineItem:
          row.lineItemName === null
            ? { text: t('effort.task.noLineItem'), muted: true }
            : {
                text: row.lineItemName,
                muted: true,
                ...(row.contractIdentifier === null ? {} : { sub: row.contractIdentifier }),
              },
        assignee: row.assigneeName === null ? null : { text: row.assigneeName, muted: true },
        status: { text: row.statusName },
        estimate: figure(hours(Number(row.estimate)), { muted: true }),
        logged: logged > 0.004 ? figure(hours(logged), { bold: true }) : null,
        /* ⚠️ The weight is shown rather than only applied — it is the one input to the earned
           value that a reader has no other way to check. */
        weight: figure(index(Number(row.weight)), { muted: true }),
        earned: figure(hours(earned)),
        variance: figure(signedHours(gap), { className: bad(gap > 0.004) }),
        due:
          row.dueOn === null
            ? null
            : figure(row.dueOn, late(row) ? { className: 'text-destructive' } : { muted: true }),
      },
    };
  });

  /**
   * ⚠️⚠️ **AND IT PAGES — item 8, second pass.** *"Pagination is missing from the tasks table."*
   * A project with three hundred tasks must not draw all three hundred under a toolbar promising
   * otherwise.
   */
  const paged = useRowPages(
    rows,
    JSON.stringify([query, sort, category, status, assignee]),
    REGISTER_PAGE_SIZE,
  );

  if (view.tasks.length === 0) {
    return (
      <section className="bg-card rounded-xl border p-4 shadow-sm">
        <p className="text-muted-foreground text-center text-[12px]">{t('effort.table.noTasks')}</p>
      </section>
    );
  }

  const columns: DataListColumn[] = [
    {
      key: 'key',
      tip: tip('effort.tip.taskKey.name', 'effort.tip.taskKey.what'),
      label: t('effort.col.key'),
      width: 128,
      sortKey: 'key',
    },
    {
      key: 'title',
      tip: tip('effort.tip.task.name', 'effort.tip.task.what'),
      label: t('effort.col.task'),
      sortKey: 'title',
    },
    /**
     * ⚠️ U-4 · the deliverable, and its contract beneath, on the whole project only — where the
     * table mixes every contract's tasks and the question *whose is this* has more than one answer.
     */
    ...(view.wholeProject
      ? [
          {
            key: 'lineItem',
            tip: tip('effort.tip.taskLineItem.name', 'effort.tip.taskLineItem.what'),
            label: t('effort.col.lineItem'),
            width: 180,
            sortKey: 'lineItem',
          },
        ]
      : []),
    {
      key: 'assignee',
      tip: tip('effort.tip.assignee.name', 'effort.tip.assignee.what'),
      label: t('effort.col.assignee'),
      width: 150,
      sortKey: 'assignee',
    },
    {
      key: 'status',
      tip: tip('effort.tip.taskStatus.name', 'effort.tip.taskStatus.what'),
      label: t('effort.col.state'),
      width: 128,
      sortKey: 'status',
    },
    {
      key: 'estimate',
      tip: tip('effort.tip.taskEstimate.name', 'effort.tip.taskEstimate.what'),
      label: t('effort.col.estimate'),
      width: 92,
      align: 'right',
      sortKey: 'estimate',
    },
    {
      key: 'logged',
      tip: tip('effort.tip.taskLogged.name', 'effort.tip.taskLogged.what'),
      label: t('effort.col.logged'),
      width: 100,
      align: 'right',
      sortKey: 'logged',
    },
    {
      key: 'weight',
      tip: tip('effort.tip.weight.name', 'effort.tip.weight.what'),
      label: t('effort.col.weight'),
      width: 72,
      align: 'right',
      sortKey: 'weight',
    },
    {
      key: 'earned',
      tip: tip('effort.tip.ev.name', 'effort.tip.ev.what'),
      label: t('effort.col.earned'),
      width: 112,
      align: 'right',
      sortKey: 'earned',
    },
    {
      key: 'variance',
      tip: tip('effort.tip.taskVariance.name', 'effort.tip.taskVariance.what'),
      label: t('effort.col.variance'),
      width: 96,
      align: 'right',
      sortKey: 'variance',
    },
    {
      key: 'due',
      tip: tip('effort.tip.due.name', 'effort.tip.due.what'),
      label: t('effort.col.due'),
      width: 104,
      align: 'right',
      sortKey: 'due',
    },
  ];

  const filtered = query.trim() !== '' || category !== '' || status !== '' || assignee !== '';

  return (
    <Register title={t('effort.table.tasks')}>
      <DataList
        /* 30 Oct 2026 · sized to its rows: `h-full` took the card's whole height BELOW its header,
           so the pager overflowed the card and was clipped — only ten rows could ever be seen. */
        className="h-auto"
        columnsKey="effort.tasks"
        selectable={false}
        columns={columns}
        rows={paged.rows}
        page={paged.page}
        sort={sort}
        onSortChange={setSort}
        search={query}
        onSearchChange={setQuery}
        filters={[
          {
            key: 'assignee',
            label: t('effort.filter.assignee'),
            value: assignee,
            onChange: setAssignee,
            options: distinct(
              view.tasks.map((one) => one.assigneeName),
              t('effort.filter.unassigned'),
            ),
          },
          {
            key: 'status',
            label: t('effort.filter.status'),
            value: status,
            onChange: setStatus,
            options: distinct(
              view.tasks.map((one) => one.statusName),
              t('effort.group.none'),
            ),
          },
          {
            key: 'category',
            label: t('effort.filter.category'),
            value: category,
            onChange: setCategory,
            options: [
              { value: 'open', label: t('status.category.open') },
              { value: 'in_progress', label: t('status.category.inProgress') },
              { value: 'in_review', label: t('status.category.inReview') },
              { value: 'closed', label: t('status.category.closed') },
            ],
          },
        ]}
        onRowClick={onOpenTask}
        empty={
          filtered
            ? {
                icon: 'search',
                title: t('effort.tools.noMatch'),
                body: t('effort.tools.noMatchBody'),
              }
            : { icon: 'search', title: t('effort.table.noTasks'), body: '' }
        }
        footerNote={t('effort.table.lateNote', {
          late: String(shown.filter((one) => late(one)).length),
        })}
        labels={{ ...listLabels(t), table: t('effort.table.tasks') }}
      />
    </Register>
  );
}

/* ── the people, and how the work is split between them ────────────────────── */

/** ⚠️ One shape for a person and for a band — `EffortResourcePanel` argues why. */
interface PeopleRow {
  key: string;
  label: string;
  sub: string;
  seed: string;
  /** The project role's own colour on a role group; `null` falls back to the seed's. */
  color: string | null;
  /**
   * ⚠️⚠️ **C68 · `null` ON A GROUP ROW, AND THAT IS THE WHOLE POINT.** This table is a
   * person breakdown OR a band breakdown depending on `grouping`, and only one of those
   * has a face. A generated face on *Senior* or on *QA* is a face on a category — the
   * same rule that keeps one off a project swatch and a client's mark.
   */
  avatarSeed: string | null;
  headcount: number;
  planned: number;
  logged: number;
  overtime: number;
}

/** ⚠️ Round 5 item 10 — the three the product owner named, in the order they were named. */
const GROUPINGS = ['person', 'seniority', 'role'] as const;
type Grouping = (typeof GROUPINGS)[number];

export function EffortResourcePanel({
  view,
  model,
}: {
  readonly view: ContractEffort;
  readonly model: EffortModel;
}) {
  const t = useT();
  const tip = useHeadTip();
  const [grouping, setGrouping] = useState<Grouping>('person');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  /* ⚠️ ux-pass-2 — the two bands a person on the wire carries, as filters. They narrow the PEOPLE
     before grouping, so *Szerepkör: QA* grouped by seniority answers "how senior is our QA". */
  const [role, setRole] = useState('');
  const [seniority, setSeniority] = useState('');

  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ GROUPED BY PERSON, SENIORITY OR ROLE — round 5 item 10.                 │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * *"Lehessen állítani ember, szenioritás, szerepkör szerint"*, from a view selector in the
   * corner — the shape the financial dashboard already has.
   *
   * ⚠️⚠️ **ONE ROW SHAPE FOR ALL THREE, WHICH IS WHY THE TABLE BELOW DID NOT CHANGE.** A
   * person is a group of one: same four figures, same bars, same columns. The alternative — a
   * second table for the grouped views — is two places to fix every column from here on.
   *
   * ⚠️ **A missing band groups under *Nincs megadva* rather than vanishing.** Somebody with no
   * seniority set is still working on the contract, and a table whose rows do not add up to the
   * headcount is a table nobody can trust.
   */
  const groupKeyOf = (person: EffortPerson): string =>
    grouping === 'seniority'
      ? (person.seniority ?? '')
      : grouping === 'role'
        ? (person.roleName ?? '')
        : person.memberId;

  const people = view.people.filter(
    (person) =>
      picks(person.roleName, role) &&
      picks(person.seniority, seniority) &&
      matches([person.name, person.roleName], query),
  );

  const grouped = [...people]
    .reduce<PeopleRow[]>((acc, person) => {
      const key = groupKeyOf(person);
      const found = acc.find((one) => one.key === key);
      const target =
        found ??
        ({
          key,
          /* ⚠️ On a person row the label is their name; on a group it is the band, or the
           *not set* wording where they have none. */
          label:
            grouping === 'person'
              ? person.name
              : key === ''
                ? /* ⚠️ The role is the PROJECT role — none is its own named group. */
                  grouping === 'role'
                  ? t('effort.role.none')
                  : t('effort.group.none')
                : grouping === 'seniority'
                  ? seniorityName(t, key)
                  : key,
          /* ⚠️ A person's own role under their name, as before. A group's subtitle is its size,
             which is the fact a grouped row gains and a person row cannot have. */
          sub: grouping === 'person' ? (person.roleName ?? '–') : '',
          /* ⚠️ The identity colour is seeded from the GROUP's key, so the same band keeps the
             same colour between renders — `identityColor` hashes whatever it is given. */
          seed: key === '' ? 'none' : key,
          /* ⚠️ A project role has a colour of its own, and the role group wears it. */
          color: grouping === 'role' ? person.roleColor : null,
          avatarSeed: grouping === 'person' ? person.avatarSeed : null,
          headcount: 0,
          planned: 0,
          logged: 0,
          overtime: 0,
        } satisfies PeopleRow);
      if (found === undefined) acc.push(target);
      target.headcount += 1;
      target.planned += Number(person.planned);
      target.logged += Number(person.logged);
      target.overtime += Number(person.overtime);
      return acc;
    }, [])
    .map((row) => ({
      ...row,
      sub:
        grouping === 'person'
          ? row.sub
          : t('effort.panel.headcount', { count: String(row.headcount) }),
    }))
    /* ⚠️ The order an UNSORTED table keeps — biggest first, as before the list had headers. */
    .sort((a, b) => Math.max(b.planned, b.logged) - Math.max(a.planned, a.logged));

  const scale = Math.max(1, ...grouped.map((one) => Math.max(one.planned, one.logged)));

  const rows: DataListRow[] = sortInMemory(grouped, sort, {
    person: (row) => row.label,
    planActual: (row) => (row.planned > 0.004 ? row.logged / row.planned : null),
    plan: (row) => row.planned,
    actual: (row) => row.logged,
    variance: (row) => row.logged - row.planned,
    utilisation: (row) => (row.planned > 0.004 ? row.logged / row.planned : null),
    overtime: (row) => row.overtime,
    ofFrame: (row) => (model.budget > 0.004 ? row.logged / model.budget : null),
  }).map((row) => {
    const gap = row.logged - row.planned;
    /* ⚠️ 108%, the mockup's own threshold — a person a few per cent over their plan is a
       rounding artefact of the calendar, not something to flag. */
    const utilisation = row.planned > 0.004 ? row.logged / row.planned : null;
    const hot = utilisation !== null && utilisation > 1.08;
    return {
      id: row.key === '' ? NONE : row.key,
      cells: {
        person: {
          text: row.label,
          bold: true,
          ...(row.sub === '' ? {} : { sub: row.sub }),
          avatar: {
            initials: initialsOf(row.label),
            color: row.color ?? identityColor(row.seed),
            ...(row.avatarSeed === null ? {} : { seed: row.avatarSeed }),
          },
        },
        /**
         * ⚠️⚠️ **ONE BAR WHERE THERE WERE TWO.** The plan above and the actual below on one shared
         * scale; `Cell.progress` draws a single bar. It is the ACTUAL on that same scale, red past
         * 108% of plan, so the length still compares across rows and the colour still flags the
         * overrun — what is lost is the plan's own rule beside it, which the next column prints.
         */
        planActual: {
          progress: {
            value: Math.round((row.logged / scale) * 100),
            color: hot ? 'var(--destructive)' : 'var(--primary)',
            label: t('effort.col.planActual'),
          },
        },
        plan: row.planned > 0.004 ? figure(hours(row.planned)) : null,
        actual: row.logged > 0.004 ? figure(hours(row.logged), { bold: true }) : null,
        variance: figure(
          signedHours(gap),
          gap > 0.004 ? { className: 'text-destructive' } : { muted: true },
        ),
        utilisation:
          utilisation === null ? null : figure(percent(utilisation), { className: bad(hot) }),
        overtime: row.overtime > 0.004 ? figure(hours(row.overtime)) : null,
        ofFrame:
          model.budget > 0.004 ? figure(percent(row.logged / model.budget), { muted: true }) : null,
      },
    };
  });

  const paged = useRowPages(
    rows,
    JSON.stringify([grouping, query, sort, role, seniority]),
    REGISTER_PAGE_SIZE,
  );

  const columns: DataListColumn[] = [
    {
      key: 'person',
      tip: tip('effort.tip.person.name', 'effort.tip.person.what'),
      label: t('effort.col.person'),
      sortKey: 'person',
    },
    {
      key: 'planActual',
      tip: tip('effort.tip.planActual.name', 'effort.tip.planActual.what'),
      label: t('effort.col.planActual'),
      width: 140,
      sortKey: 'planActual',
    },
    {
      key: 'plan',
      tip: tip('effort.tip.personPlan.name', 'effort.tip.personPlan.what'),
      label: t('effort.col.plan'),
      width: 88,
      align: 'right',
      sortKey: 'plan',
    },
    {
      key: 'actual',
      tip: tip('effort.tip.personActual.name', 'effort.tip.personActual.what'),
      label: t('effort.col.actual'),
      width: 88,
      align: 'right',
      sortKey: 'actual',
    },
    {
      key: 'variance',
      tip: tip('effort.tip.personVariance.name', 'effort.tip.personVariance.what'),
      label: t('effort.col.variance'),
      width: 96,
      align: 'right',
      sortKey: 'variance',
    },
    {
      key: 'utilisation',
      tip: tip('effort.tip.utilisation.name', 'effort.tip.utilisation.what'),
      label: t('effort.col.utilisation'),
      width: 120,
      align: 'right',
      sortKey: 'utilisation',
    },
    {
      key: 'overtime',
      tip: tip('effort.tip.overtime.name', 'effort.tip.overtime.what'),
      label: t('effort.col.overtime'),
      width: 88,
      align: 'right',
      sortKey: 'overtime',
    },
    {
      key: 'ofFrame',
      tip: tip('effort.tip.ofFrame.name', 'effort.tip.ofFrame.what'),
      label: t('effort.col.ofFrame'),
      width: 100,
      align: 'right',
      sortKey: 'ofFrame',
    },
  ];

  const filtered = query.trim() !== '' || role !== '' || seniority !== '';

  /*
   * ⚠️⚠️ **THE DONUT'S ARITHMETIC WENT WITH THE DONUT — round 5 item 10.** It divided the
   * logged hours between people as conic-gradient segments, and fell back to the PLAN on a
   * contract that had not started so an empty ring never appeared. That fallback was the careful
   * part and it is what makes its removal worth a note: nothing here was broken, it was a second
   * drawing of what the table below already lists person by person.
   */

  return (
    <div className="flex flex-col gap-3">
      <Register
        title={t('effort.panel.people')}
        aside={
          /* ⚠️⚠️ **IN THE CORNER, WHICH IS WHERE THE PRODUCT OWNER POINTED** — *"mint ahogy a
              pénzügyi dashboardon van most"*. The headcount it replaces is now the subtitle of
              every grouped row, and on the person view it is simply the number of rows. */
          <DropdownMenu
            width={220}
            align="end"
            label={t('effort.group.label')}
            items={GROUPINGS.map((one) => ({
              label: groupingLabel(t, one),
              checked: one === grouping,
              onSelect: () => {
                setGrouping(one);
              },
            }))}
          >
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-[11.5px] transition-colors"
            >
              {groupingLabel(t, grouping)}
              <Icon name="chevron-down" size={12} className="opacity-60" />
            </button>
          </DropdownMenu>
        }
      >
        <DataList
          /* 30 Oct 2026 · sized to its rows: `h-full` took the card's whole height BELOW its header,
             so the pager overflowed the card and was clipped — only ten rows could ever be seen. */
          className="h-auto"
          columnsKey="effort.people"
          selectable={false}
          columns={columns}
          rows={paged.rows}
          page={paged.page}
          sort={sort}
          onSortChange={setSort}
          search={query}
          onSearchChange={setQuery}
          filters={[
            {
              key: 'role',
              label: t('effort.filter.role'),
              value: role,
              onChange: setRole,
              options: distinct(
                view.people.map((one) => one.roleName),
                t('effort.role.none'),
              ),
            },
            {
              key: 'seniority',
              label: t('effort.filter.seniority'),
              value: seniority,
              onChange: setSeniority,
              options: distinct(
                view.people.map((one) => one.seniority),
                t('effort.group.none'),
              ).map((one) =>
                one.value === NONE ? one : { value: one.value, label: seniorityName(t, one.value) },
              ),
            },
          ]}
          empty={
            filtered
              ? {
                  icon: 'search',
                  title: t('effort.tools.noMatch'),
                  body: t('effort.tools.noMatchBody'),
                }
              : { icon: 'users', title: t('effort.panel.noPeople'), body: '' }
          }
          labels={{ ...listLabels(t), table: t('effort.panel.people') }}
        />
      </Register>

      {/**
       * ┌────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ *RÖGZÍTETT MEGOSZLÁS* IS DELETED — round 5 item 10: *"kuka"*.       │
       * └────────────────────────────────────────────────────────────────────┘
       *
       * A donut splitting logged hours between people, beside a table that lists the same people
       * with the same hours and four more columns. It fell back to the PLAN on a contract that had
       * not started, renaming itself as it went — careful work, and a second reading of one fact.
       *
       * ⚠️ **The table takes the whole width with it**, which is the other half of the item: it
       * was two of three columns and is now the section.
       */}
    </div>
  );
}

/** ⚠️ One `t()` per branch with a literal key — the i18n rule, as everywhere else. */
function groupingLabel(t: Translate, grouping: Grouping): string {
  if (grouping === 'seniority') return t('effort.group.seniority');
  if (grouping === 'role') return t('effort.group.role');
  return t('effort.group.person');
}

/**
 * ⚠️ The ladder's own words where the value is on it. `seniority` is a free string on this wire,
 * so anything else prints as stored rather than being forced onto a rung it is not.
 */
function seniorityName(t: Translate, level: string): string {
  if (level === 'junior') return t('person.seniority.junior');
  if (level === 'medior') return t('person.seniority.medior');
  if (level === 'senior') return t('person.seniority.senior');
  if (level === 'lead') return t('person.seniority.lead');
  if (level === 'principal') return t('person.seniority.principal');
  return level;
}
